/**
 * Thin DataGridPro wrapper for RAT grids.
 *
 * Owns shared look-and-feel (`ratDataGridSx` — header/footer tint, vertical
 * cell centering, pinned header match), optional localStorage persistence, and
 * RATGridToolbar wiring. Pass `sx` to override any kit default (merged after
 * `ratDataGridSx`). Domain columns, row fetching, and edit handlers stay in the
 * feature that configures the grid.
 *
 * Typical setup: field enum → buildColumns() → persistence config → toolbar flags.
 */
import React, { useCallback, useMemo } from "react";
import {
  DataGridPro,
  DataGridProProps,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridColDef,
  GridRowId,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import {
  GridPersistenceConfig,
  useRATDataGridStatePersistence,
} from "./useRATDataGridStatePersistence";
import {
  RATDataGridToolbar,
  RATDataGridToolbarProps,
} from "./RATDataGridToolbar";
import { RATDataGridDetailPanelCollapseAllHeader } from "./RATDataGridDetailPanelCollapseAllHeader";
import { scheduleResetRowHeightsAfterDetailCollapse } from "./ratDataGrid.util";
import { ratDataGridSx } from "./ratDataGrid.sx";

/** Toolbar props; onResetLayout is optional because persistence can supply it. */
export type RATDataGridToolbarConfig = Omit<
  RATDataGridToolbarProps,
  "onResetLayout"
> & {
  readonly onResetLayout?: () => void;
};

export type RATDataGridProps<R extends GridValidRowModel = GridValidRowModel> =
  Omit<DataGridProProps<R>, "slots" | "apiRef" | "initialState" | "sx"> & {
    readonly apiRef?: DataGridProProps<R>["apiRef"];
    readonly initialState?: DataGridProProps<R>["initialState"];
    /** When set, restores/saves layout via localStorage and enables Reset Layout. */
    readonly persistence?: GridPersistenceConfig;
    /**
     * Toolbar config. Pass `false` to hide the toolbar entirely.
     * Omit with persistence enabled to get a minimal toolbar that still
     * exposes Reset Layout from persistence.
     */
    readonly toolbar?: RATDataGridToolbarConfig | false;
    readonly slots?: DataGridProProps<R>["slots"];
    /**
     * Merged after `ratDataGridSx`. Use to extend or override kit defaults
     * (e.g. `{ "& .MuiDataGrid-footerContainer": { backgroundColor: "white" } }`).
     */
    readonly sx?: DataGridProProps<R>["sx"];
    /**
     * When expandable rows are enabled (`getDetailPanelContent`), show a
     * collapse-all control in the detail-panel toggle column header.
     * Defaults to `true` whenever `getDetailPanelContent` is set; pass `false`
     * to keep MUI’s empty toggle header.
     */
    readonly showCollapseAllDetailPanels?: boolean;
  };

/**
 * Ensures the master-detail toggle column has a collapse-all header when
 * detail panels are enabled. Respects a caller-supplied `renderHeader` on an
 * existing toggle column.
 */
function withCollapseAllDetailPanelHeader<R extends GridValidRowModel>(
  columns: readonly GridColDef<R>[] | undefined
): GridColDef<R>[] {
  const baseColumns = columns ? [...columns] : [];
  const toggleColumn: GridColDef<R> = {
    ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    renderHeader: () => <RATDataGridDetailPanelCollapseAllHeader />,
  };

  const existingIndex = baseColumns.findIndex(
    (column) => column.field === GRID_DETAIL_PANEL_TOGGLE_FIELD
  );

  if (existingIndex >= 0) {
    const existing = baseColumns[existingIndex];
    baseColumns[existingIndex] = {
      ...toggleColumn,
      ...existing,
      // Keep an explicit caller header; otherwise use collapse-all.
      renderHeader: existing.renderHeader ?? toggleColumn.renderHeader,
    };
    return baseColumns;
  }

  return [toggleColumn, ...baseColumns];
}

/** Merge base + caller sx (MUI accepts an array of SxProps). */
function mergeSx(
  ...parts: Array<DataGridProProps["sx"] | undefined>
): DataGridProProps["sx"] {
  const defined = parts.filter(
    (part): part is NonNullable<DataGridProProps["sx"]> => part != null
  );
  if (defined.length === 0) {
    return undefined;
  }
  if (defined.length === 1) {
    return defined[0];
  }
  return defined as DataGridProProps["sx"];
}

export function RATDataGrid<R extends GridValidRowModel = GridValidRowModel>({
  persistence,
  toolbar,
  slots,
  apiRef: externalApiRef,
  initialState: externalInitialState,
  sx,
  showCollapseAllDetailPanels,
  disableRowSelectionOnClick = true,
  showColumnVerticalBorder = true,
  showCellVerticalBorder = true,
  onDetailPanelExpandedRowIdsChange,
  ...dataGridProps
}: RATDataGridProps<R>) {
  // Always call the hook (hooks rules); pass undefined when persistence is off.
  const {
    apiRef: persistenceApiRef,
    initialState: persistenceInitialState,
    layoutKey,
    resetGridState,
  } = useRATDataGridStatePersistence(persistence);

  const apiRef = externalApiRef ?? persistenceApiRef;
  // Persistence owns initialState when enabled so restore/reset stay consistent.
  const initialState = persistence
    ? persistenceInitialState
    : externalInitialState;

  const hasDetailPanels = Boolean(dataGridProps.getDetailPanelContent);

  // Collapse-all header is on whenever detail panels are enabled, unless opted out.
  const shouldShowCollapseAll =
    hasDetailPanels && showCollapseAllDetailPanels !== false;

  const columns = useMemo(
    () =>
      shouldShowCollapseAll
        ? withCollapseAllDetailPanelHeader(dataGridProps.columns)
        : dataGridProps.columns,
    [shouldShowCollapseAll, dataGridProps.columns]
  );

  const resolvedToolbar = useMemo(() => {
    if (toolbar === false) {
      return undefined;
    }

    // No toolbar config and no persistence → leave DataGrid without a toolbar slot.
    if (!toolbar && !persistence) {
      return undefined;
    }

    // Persistence-only: empty toolbar defaults, Reset Layout wired below.
    const toolbarConfig: RATDataGridToolbarConfig = toolbar ?? {
      showAddButton: false,
      showApplySortButton: false,
      showClearSortButton: false,
      showColumnsButton: false,
      showFilterButton: false,
      showDensityButton: false,
    };

    return () => (
      <RATDataGridToolbar
        {...toolbarConfig}
        onResetLayout={
          toolbarConfig.onResetLayout ?? resetGridState ?? undefined
        }
        showResetLayout={
          toolbarConfig.showResetLayout ?? Boolean(resetGridState)
        }
      />
    );
  }, [toolbar, persistence, resetGridState]);

  // Same virtualizer fix as collapse-all: after any panel closes, recalculate
  // row heights so rows below do not stay blank until scroll.
  const handleDetailPanelExpandedRowIdsChange = useCallback(
    (
      ids: GridRowId[],
      details: Parameters<
        NonNullable<DataGridProProps["onDetailPanelExpandedRowIdsChange"]>
      >[1]
    ) => {
      const previousIds = apiRef.current.getExpandedDetailPanels();
      const isCollapsing = previousIds.some((id) => !ids.includes(id));

      onDetailPanelExpandedRowIdsChange?.(ids, details);

      if (isCollapsing) {
        scheduleResetRowHeightsAfterDetailCollapse(apiRef, () => {
          const current = apiRef.current.getExpandedDetailPanels();
          return (
            current.length === ids.length &&
            ids.every((id) => current.includes(id))
          );
        });
      }
    },
    [apiRef, onDetailPanelExpandedRowIdsChange]
  );

  return (
    <DataGridPro
      // layoutKey remounts the grid so Reset Layout can re-apply initialState.
      key={layoutKey}
      apiRef={apiRef}
      initialState={initialState}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      showColumnVerticalBorder={showColumnVerticalBorder}
      showCellVerticalBorder={showCellVerticalBorder}
      {...dataGridProps}
      columns={columns}
      onDetailPanelExpandedRowIdsChange={
        hasDetailPanels
          ? handleDetailPanelExpandedRowIdsChange
          : onDetailPanelExpandedRowIdsChange
      }
      slots={{
        ...slots,
        ...(resolvedToolbar ? { toolbar: resolvedToolbar } : {}),
      }}
      sx={mergeSx(ratDataGridSx, sx)}
    />
  );
}

export default RATDataGrid;
